import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { Bell, Check, Trash2 } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

const NotificationBell = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Track viewport so we can mount the mobile Sheet *only* on small screens.
  // Previously the Sheet was always mounted; its portalled overlay (bg-black/80)
  // covered the desktop dashboard even though `SheetContent` had `sm:hidden`
  // — the overlay itself is a sibling and doesn't inherit that class.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const dropdownRef = useRef(null);

  // Fetch unread count on mount and periodically
  const fetchUnreadCount = async () => {
    try {
      const response = await authAxios.get("/notifications/unread-count");
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  // Fetch notifications when dropdown opens
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get("/notifications?limit=10");
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read.
  // The mark-as-read API is best-effort: navigation must always succeed even
  // if the notification was already read (the backend used to return 404 in
  // that case, which previously short-circuited the navigate() call and made
  // clicks look completely inert). We now optimistically update local state,
  // fire the read API without blocking, and always navigate when a link is
  // present.
  const markAsRead = async (notificationId, link) => {
    const target = notifications.find((n) => n.notification_id === notificationId);
    const wasUnread = !!target && !target.read;

    // Optimistic UI: mark as read locally and decrement badge if it was unread
    setNotifications((prev) =>
      prev.map((n) => (n.notification_id === notificationId ? { ...n, read: true } : n))
    );
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    // Fire-and-forget the read API. A 404 means "already read" on the backend
    // — benign, so we silence it. Any other failure is logged but never blocks
    // navigation.
    authAxios.post(`/notifications/${notificationId}/read`).catch((error) => {
      if (error?.response?.status !== 404) {
        console.error("Error marking as read:", error);
      }
    });

    if (link) {
      navigate(link);
      setIsOpen(false);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authAxios.post("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Clear all notifications (with confirmation — bulk delete is destructive)
  const clearAll = async () => {
    if (notifications.length === 0) return;
    const ok = window.confirm(
      `Delete all ${notifications.length} notification${notifications.length === 1 ? "" : "s"}?\n\nThis cannot be undone.`
    );
    if (!ok) return;
    try {
      await authAxios.delete("/notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  // Delete a single notification (used by mobile swipe-to-dismiss).
  // Optimistically removes from state; on failure, the next fetch restores it.
  const deleteOne = async (notificationId) => {
    const target = notifications.find((n) => n.notification_id === notificationId);
    setNotifications((prev) => prev.filter((n) => n.notification_id !== notificationId));
    if (target && !target.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    try {
      await authAxios.delete(`/notifications/${notificationId}`);
    } catch (error) {
      console.error("Error deleting notification:", error);
      // Best-effort: re-fetch to re-sync state on failure
      fetchNotifications();
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch when dropdown/sheet opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside (DESKTOP only — Sheet has its own
  // overlay-dismiss; if we also listened here on mobile, the click that
  // opens the Sheet would race the outside-click handler).
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't run outside-click logic on mobile — the Sheet is portalled
      // outside our ref so any tap would close it immediately.
      if (typeof window !== "undefined" && window.innerWidth < 640) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Track viewport breakpoint so the mobile Sheet only mounts when needed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon color based on notification type
  const getTypeColor = (type) => {
    switch (type) {
      case "NEW_POST": return "bg-blue-500";
      case "COMMENT_REPLY": return "bg-green-500";
      case "POST_COMMENT": return "bg-purple-500";
      case "POST_LIKE": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  // Mobile swipe-to-dismiss row. Native touch events — no framer-motion
  // dependency. Drag left to reveal a rose "Delete" backdrop; release past
  // the threshold to commit, otherwise snap back. Tap (no drag) routes to
  // markAsRead for normal navigation.
  const SwipeableRow = ({ notif }) => {
    const [dragX, setDragX] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [removing, setRemoving] = useState(false);
    const startXRef = useRef(0);
    const movedRef = useRef(false);
    const SWIPE_THRESHOLD = 80;

    const handleTouchStart = (e) => {
      startXRef.current = e.touches[0].clientX;
      movedRef.current = false;
      setDragging(true);
    };
    const handleTouchMove = (e) => {
      const dx = e.touches[0].clientX - startXRef.current;
      // Only allow leftward drag; ignore rightward
      const clamped = Math.min(0, dx);
      if (Math.abs(clamped) > 6) movedRef.current = true;
      setDragX(clamped);
    };
    const handleTouchEnd = () => {
      setDragging(false);
      if (dragX <= -SWIPE_THRESHOLD) {
        // Commit: animate fully off-screen, then delete
        setRemoving(true);
        setDragX(-500);
        setTimeout(() => deleteOne(notif.notification_id), 200);
      } else {
        setDragX(0);
      }
    };
    const handleClick = () => {
      // Suppress click-as-tap if the user actually swiped meaningfully
      if (movedRef.current && Math.abs(dragX) > 6) return;
      markAsRead(notif.notification_id, notif.link);
    };

    return (
      <div className="relative overflow-hidden" data-testid={`notif-row-${notif.notification_id}`}>
        {/* Rose backdrop revealed during left-swipe */}
        <div className="absolute inset-0 flex items-center justify-end pr-6 bg-rose-600/80 pointer-events-none">
          <Trash2 className="w-5 h-5 text-white" />
          <span className="ml-2 text-white text-sm font-medium">Delete</span>
        </div>
        <button
          type="button"
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          data-testid={`notif-item-${notif.notification_id}`}
          style={{
            transform: `translateX(${dragX}px)`,
            transition: dragging ? "none" : (removing ? "transform 200ms ease-out" : "transform 200ms ease-out"),
          }}
          className={`relative w-full text-left px-4 py-3 hover:bg-white/5 bg-divine-charcoal ${
            !notif.read ? "bg-white/[0.07]" : ""
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${getTypeColor(notif.type)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm font-medium truncate ${
                  !notif.read ? "text-white" : "text-white/70"
                }`}>
                  {notif.title}
                </p>
                {!notif.read && (
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-white/50 mt-0.5 line-clamp-2">
                {notif.message}
              </p>
              <p className="text-xs text-white/30 mt-1">
                {formatTimeAgo(notif.created_at)}
              </p>
            </div>
          </div>
        </button>
      </div>
    );
  };

  // Shared header. The mobile Sheet variant ships with shadcn's built-in
  // X close in the top-right corner, so we don't render our own — that
  // avoided a Playwright pointer-interception bug from overlapping icons.
  // `isMobile=true` enlarges the trash icon and adds left-margin so it
  // doesn't crowd the shadcn close-X (which sits at `right-4 top-4`).
  const ListHeader = ({ isMobile = false }) => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
      <h3
        className={`font-semibold ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}
        data-testid="notification-panel-title"
      >
        Notifications
      </h3>
      <div className={`flex items-center gap-3 ${isMobile ? "mr-10" : ""}`}>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs text-white/60 hover:text-white flex items-center gap-1"
            title="Mark all as read"
            data-testid="notif-mark-all-read"
          >
            <Check className="w-3 h-3" />
            Mark all read
          </button>
        )}
        {notifications.length > 0 && (
          <button
            onClick={clearAll}
            className={`text-white/60 hover:text-red-400 flex items-center gap-1 transition-colors ${
              isMobile ? "p-1.5 rounded-md hover:bg-rose-500/10" : ""
            }`}
            title="Delete all notifications"
            aria-label="Delete all notifications"
            data-testid="notif-clear-all"
          >
            <Trash2 className={isMobile ? "w-5 h-5" : "w-3 h-3"} />
          </button>
        )}
      </div>
    </div>
  );

  // Shared list body — used by both desktop dropdown and mobile sheet.
  // On mobile we render the swipeable variant; on desktop the existing
  // button-based rows (hover-to-delete via header trash is fine on desktop).
  const ListBody = ({ isMobile = false }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${
            isCyber ? "border-cyan-400" : "border-divine-gold"
          }`} />
        </div>
      );
    }
    if (notifications.length === 0) {
      return (
        <div className="text-center py-12 text-white/50" data-testid="notif-empty">
          <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No notifications yet</p>
        </div>
      );
    }
    if (isMobile) {
      return (
        <div className="divide-y divide-white/5">
          {notifications.map((notif) => (
            <SwipeableRow key={notif.notification_id} notif={notif} />
          ))}
          {notifications.length > 0 ? (
            <p className="text-[10px] text-white/30 italic text-center py-2 px-4">
              Swipe a notification left to delete
            </p>
          ) : null}
        </div>
      );
    }
    return (
      <div className="divide-y divide-white/5">
        {notifications.map((notif) => (
          <button
            key={notif.notification_id}
            onClick={() => markAsRead(notif.notification_id, notif.link)}
            data-testid={`notif-item-${notif.notification_id}`}
            className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors ${
              !notif.read ? "bg-white/5" : ""
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${getTypeColor(notif.type)}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm font-medium truncate ${
                    !notif.read ? "text-white" : "text-white/70"
                  }`}>
                    {notif.title}
                  </p>
                  {!notif.read && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-white/50 mt-0.5 line-clamp-2">
                  {notif.message}
                </p>
                <p className="text-xs text-white/30 mt-1">
                  {formatTimeAgo(notif.created_at)}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const ListFooter = () => (
    notifications.length > 0 ? (
      <div className="border-t border-white/10 p-2 shrink-0">
        <button
          onClick={() => { setIsOpen(false); navigate("/news"); }}
          data-testid="notif-view-all"
          className={`w-full text-center text-sm py-2 rounded-lg transition-colors ${
            isCyber
              ? "text-cyan-400 hover:bg-cyan-500/10"
              : "text-divine-gold hover:bg-divine-gold/10"
          }`}
        >
          View all updates
        </button>
      </div>
    ) : null
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          isOpen
            ? (isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-divine-gold/20 text-divine-gold")
            : "text-white/60 hover:text-white hover:bg-white/10"
        }`}
        data-testid="notification-bell"
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* DESKTOP dropdown (≥ sm). Hidden on mobile. */}
      {isOpen && (
        <div
          className="hidden sm:block absolute right-0 top-full mt-2 w-96 bg-divine-charcoal border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notif-dropdown-desktop"
        >
          <ListHeader />
          <div className="max-h-[400px] overflow-y-auto">
            <ListBody />
          </div>
          <ListFooter />
        </div>
      )}

      {/* MOBILE slide-in Sheet (< sm). Full-height, right-anchored, with X
          close + scrollable list for long histories. Only mounted on mobile
          viewports — otherwise the Sheet's portalled overlay (bg-black/80)
          would darken the desktop dashboard when isOpen=true. */}
      {isMobile && (
        <Sheet
          open={isOpen}
          onOpenChange={(open) => {
            // Sheet only governs the mobile presentation. Avoid stomping the
            // desktop dropdown which uses its own outside-click logic.
            if (typeof window !== "undefined" && window.innerWidth >= 640) return;
            setIsOpen(open);
          }}
        >
          <SheetContent
            side="right"
            className="sm:hidden bg-divine-charcoal border-divine-gold/20 p-0 w-[88vw] max-w-sm flex flex-col"
            data-testid="notif-sheet-mobile"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Notifications</SheetTitle>
            </SheetHeader>
            <ListHeader isMobile />
            <div className="flex-1 overflow-y-auto">
              <ListBody isMobile />
            </div>
            <ListFooter />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default NotificationBell;
